import type { ByteSource } from './bytes';

/**
 * The file version of a Windows executable (`VS_FIXEDFILEINFO` in its version resource), e.g.
 * `[1, 14, 3, 71]` for the 1.14d `Game.exe`. Only the headers and the resource section are read.
 * Returns null for anything that is not a PE image with a version resource.
 */
export async function peFileVersion(src: ByteSource): Promise<[number, number, number, number] | null> {
  if (src.size < 0x40) return null;
  const dos = await src.read(0, 0x40);
  if (dos[0] !== 0x4d || dos[1] !== 0x5a) return null; // "MZ"
  const peOff = u32(dos, 0x3c);
  if (peOff <= 0 || peOff + 24 > src.size) return null;
  const nt = await src.read(peOff, 24);
  if (u32(nt, 0) !== 0x00004550) return null; // "PE\0\0"
  const sections = nt[6] | (nt[7] << 8);
  const optSize = nt[20] | (nt[21] << 8);
  const table = await src.read(peOff + 24 + optSize, sections * 40);
  for (let i = 0; i < sections; i++) {
    const s = table.subarray(i * 40, i * 40 + 40);
    if (s.length < 40) break;
    const name = String.fromCharCode(...s.subarray(0, 8)).replace(/\0+$/, '');
    if (name !== '.rsrc') continue;
    const rawSize = u32(s, 16);
    const rawPtr = u32(s, 20);
    // Resource sections are small next to the code (icons, dialogs, the version block).
    if (rawSize > 16 << 20) return null;
    return fixedFileVersion(await src.read(rawPtr, rawSize));
  }
  return null;
}

/** Finds `VS_FIXEDFILEINFO` by its signature and reads dwFileVersionMS/LS. */
function fixedFileVersion(rsrc: Uint8Array): [number, number, number, number] | null {
  for (let i = 0; i + 52 <= rsrc.length; i += 4) {
    if (u32(rsrc, i) !== 0xfeef04bd) continue;
    const ms = u32(rsrc, i + 8);
    const ls = u32(rsrc, i + 12);
    return [ms >>> 16, ms & 0xffff, ls >>> 16, ls & 0xffff];
  }
  return null;
}

function u32(b: Uint8Array, o: number): number {
  return (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;
}

/**
 * `Game.exe` file versions of the Blizzard patches, as the game names them. 1.14.3.71 (1.14d) is
 * confirmed by the 1.14d patch itself (its script refuses to patch that version or later, and its
 * own `Game.exe` carries it); the others are the published values and unverified here.
 */
const GAME_EXE_VERSIONS: Record<string, string> = {
  '1.14.3.71': '1.14d',
  '1.14.2.70': '1.14c',
  '1.14.1.68': '1.14b',
  '1.14.0.64': '1.14a',
  '1.0.13.64': '1.13d',
  '1.0.13.60': '1.13c',
  '1.0.12.49': '1.12a',
  '1.0.11.46': '1.11b',
  '1.0.11.45': '1.11',
  '1.0.10.39': '1.10',
};

/** "1.14d" for Game.exe 1.14.3.71; unknown builds give their numeric version ("1.13", "1.14"). */
export function gameVersionOf(v: readonly number[]): string {
  const known = GAME_EXE_VERSIONS[v.join('.')];
  if (known) return known;
  // 1.10–1.13 number themselves 1.0.<minor>.<build>, 1.14 onwards 1.<minor>.<letter>.<build>.
  return v[0] === 1 && v[1] === 0 ? `1.${String(v[2]).padStart(2, '0')}` : `${v[0]}.${String(v[1]).padStart(2, '0')}`;
}
